import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { Skeleton } from 'primereact/skeleton';
import { useCatalogContext } from '../../contexts/CatalogContext';
import catalogService from '../../services/catalogService';

const PriceRangeSlider = () => {
    const { filters, updateFilter, filterData } = useCatalogContext();
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(10000);
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(true);
    const [priceRangeLoaded, setPriceRangeLoaded] = useState(false);
    const fetchingRef = useRef(false);

    // Usar datos de price ranges del contexto (centralizados en useCatalogState)
    // Actualizar cuando cambie filterData.priceRanges; no bloquear por priceRangeLoaded
    useEffect(() => {
        if (filterData?.priceRanges) {
            const { precio_minimo, precio_maximo } = filterData.priceRanges;
            if (typeof precio_minimo === 'number' && typeof precio_maximo === 'number') {
                const newRange = [precio_minimo, precio_maximo];
                setPriceRange(newRange);
                setMinPrice(precio_minimo);
                setMaxPrice(precio_maximo);
                setLoading(false);
                if (!priceRangeLoaded) setPriceRangeLoaded(true); // Mantener flag solo si no estaba cargado
                console.log('✅ Rango de precios actualizado desde contexto:', newRange);
            }
        } else if (filterData && !filterData.priceRanges && !priceRangeLoaded) {
            // Sin datos de priceRanges: usar valores por defecto iniciales,
            // pero permitir que futuras actualizaciones desde el contexto los reemplacen
            console.log('⚠️ No hay datos de priceRanges, usando valores por defecto');
            setPriceRange([0, 10000]);
            setMinPrice(0);
            setMaxPrice(10000);
            setPriceRangeLoaded(true);
            setLoading(false);
        }
    }, [filterData?.priceRanges]);

    // Sincronizar con filtros externos solo cuando cambian los filtros externos
    useEffect(() => {
        if (priceRange && priceRange.length === 2 && priceRangeLoaded) {
            const externalMin = filters.precioMin || priceRange[0];
            const externalMax = filters.precioMax || priceRange[1];
            
            // Solo sincronizar si los valores externos son diferentes a los actuales
            if (externalMin !== minPrice || externalMax !== maxPrice) {
                setMinPrice(externalMin);
                setMaxPrice(externalMax);
            }
        }
    }, [filters.precioMin, filters.precioMax, priceRange, priceRangeLoaded]); // Removí minPrice y maxPrice para evitar bucles

    // Manejar cambio del slider durante el arrastre
    const handleSliderChange = useCallback((e) => {
        const value = Array.isArray(e) ? e : e.value;
        
        if (Array.isArray(value) && value.length === 2) {
            setMinPrice(value[0]);
            setMaxPrice(value[1]);
            setIsDragging(true);
        }
    }, []);

    // Manejar fin del arrastre del slider
    const handleSliderChangeEnd = useCallback((e) => {
        const value = Array.isArray(e) ? e : e.value;
        
        if (Array.isArray(value) && value.length === 2) {
            setMinPrice(value[0]);
            setMaxPrice(value[1]);
            setIsDragging(false);
            
            // Aplicar filtros solo al finalizar el arrastre
            console.log('🔄 Aplicando filtros de precio:', { min: value[0], max: value[1] });
            updateFilter('precioMin', value[0]);
            updateFilter('precioMax', value[1]);
        }
    }, [updateFilter]);

    // Manejar cambio del input de precio mínimo
    const handleMinPriceChange = useCallback((value) => {
        const numValue = value || 0;
        const validValue = Math.max(priceRange[0], Math.min(numValue, maxPrice));
        
        setMinPrice(validValue);
        updateFilter('precioMin', validValue);
    }, [priceRange, maxPrice, updateFilter]);

    // Manejar cambio del input de precio máximo
    const handleMaxPriceChange = useCallback((value) => {
        const numValue = value || priceRange[1];
        const validValue = Math.min(priceRange[1], Math.max(numValue, minPrice));
        
        setMaxPrice(validValue);
        updateFilter('precioMax', validValue);
    }, [priceRange, minPrice, updateFilter]);

    if (loading) {
        return (
            <div className="p-4">
                <Skeleton width="100%" height="2rem" className="mb-3" />
                <Skeleton width="100%" height="3rem" className="mb-3" />
                <div className="flex gap-2">
                    <Skeleton width="50%" height="2.5rem" />
                    <Skeleton width="50%" height="2.5rem" />
                </div>
            </div>
        );
    }

    return (
        <div className="p-0">
            <div className="mb-1">
                <label className="block text-sm font-medium text-secondary mb-2">
                    Rango: S/ {minPrice} - S/ {maxPrice}
                </label>
                {/** Asegurar que los límites visuales incluyan los filtros actuales **/}
                {(() => {
                    const uiMin = Math.min(priceRange[0], (minPrice ?? priceRange[0]));
                    const uiMax = Math.max(priceRange[1], (maxPrice ?? priceRange[1]));
                    return (
                        <Slider
                            value={[minPrice, maxPrice]}
                            onChange={handleSliderChange}
                            onSlideEnd={handleSliderChangeEnd}
                            range
                            min={uiMin}
                            max={uiMax}
                            step={100}
                            className="w-full"
                        />
                    );
                })()}
            </div>
            
            <div className="flex gap-2">
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Mínimo</label>
                    <InputNumber
                        value={minPrice}
                        onValueChange={(e) => handleMinPriceChange(e.value)}
                        mode="currency"
                        currency="PEN"
                        locale="es-PE"
                        min={Math.min(priceRange[0], (minPrice ?? priceRange[0]))}
                        max={Math.max(priceRange[1], (maxPrice ?? priceRange[1]))}
                        className="w-full"
                        inputClassName="w-full text-sm"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Máximo</label>
                    <InputNumber
                        value={maxPrice}
                        onValueChange={(e) => handleMaxPriceChange(e.value)}
                        mode="currency"
                        currency="PEN"
                        locale="es-PE"
                        min={Math.min(priceRange[0], (minPrice ?? priceRange[0]))}
                        max={Math.max(priceRange[1], (maxPrice ?? priceRange[1]))}
                        className="w-full"
                        inputClassName="w-full text-sm"
                    />
                </div>
            </div>
        </div>
    );
};

export default PriceRangeSlider;